import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Avatar, Button, IconButton, Chip, TextField, Divider,
  CircularProgress, Alert, Tooltip, Autocomplete, Collapse, Badge,
  Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab,
  FormControl, InputLabel, Select, MenuItem, List, ListItem, ListItemText,
  ListItemAvatar, ListItemIcon, Paper, Card, CardContent, Grid,
} from '@mui/material';
import {
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  LocalHospital as HospitalIcon,
  CalendarToday as CalendarIcon,
  MedicalServices as MedicalIcon,
  Description as DocIcon,
  Receipt as ReceiptIcon,
  Science as ScienceIcon,
  Favorite as HeartIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  EventAvailable as EventIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Cake as CakeIcon,
  Assignment as AssignmentIcon,
  AttachMoney as MoneyIcon,
  Send as SendIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingIcon,
  Bloodtype as BloodIcon,
  Medication as MedicationIcon,
  Vaccines as VaccinesIcon,
  EventNote as EventNoteIcon,
  WhatsApp as WhatsAppIcon,
  UploadFile as UploadFileIcon,
  Download as DownloadIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  ZoomIn as ZoomInIcon,
  Star as StarIcon,
  Share as ShareIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon2,
  NoteAlt as NoteAltIcon,
  CheckCircle as CheckCircleIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';
import { resolveEvolutionEntryFormat } from '../utils/clinicEvolutionFormat';
import PatientAvatarUpload from './PatientAvatarUpload';
import TeleconsultButton from './Teleconsulta/TeleconsultButton';
import SendDocumentDialog from './SendDocumentDialog';
import HealthcareEvolutionDialog from './HealthcareEvolutionDialog';
import ClinicalHtmlField from './ClinicalHtmlField';
import { clinicalHtmlToPlainPreview } from '../utils/clinicalHtml';

interface HealthcarePanelProps {
  contactId: string | null;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  onSendMessage?: (message: string) => void;
  onSendMediaUrl?: (url: string, type: string, fileName?: string) => void;
  onClose?: () => void;
  open: boolean;
  /** Aba inicial ao abrir (0=perfil, 1=agenda, …) */
  initialTab?: number;
  /** Abre o diálogo de agendamento rápido quando já houver paciente vinculado */
  openScheduleOnMount?: boolean;
  onPatientLinked?: () => void;
}

interface Patient {
  id: string;
  full_name: string;
  social_name?: string;
  cpf?: string;
  rg?: string;
  birth_date?: string;
  gender?: string;
  phone?: string;
  whatsapp?: string;
  email?: string;
  blood_type?: string;
  allergies?: string[];
  chronic_conditions?: string[];
  medications?: string[];
  medical_notes?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  insurance_plan?: string;
  insurance_number?: string;
  status: string;
  total_visits: number;
  total_spent: number;
  avatar_url?: string;
  contact_id?: string;
  first_visit_date?: string;
  last_visit_date?: string;
  tags?: string[];
}

interface HealthcareSummary {
  patient: Patient;
  upcoming_appointments: any[];
  recent_appointments: any[];
  recent_budgets: any[];
  recent_prescriptions: any[];
  recent_exam_orders: any[];
  active_treatment_plans: any[];
  recent_certificates: any[];
  recent_evolutions: any[];
  financial: {
    total_paid: number;
    total_pending: number;
    total_overdue: number;
    transactions_count: number;
    recent_transactions?: any[];
    pending_discounts?: any[];
    professional_fees?: any[];
    checks_received?: any[];
  };
  stats: {
    total_appointments: number;
    completed_appointments: number;
    total_budgets: number;
    total_prescriptions: number;
    total_procedure_items?: number;
  };
  patient_procedures?: {
    pending: any[];
    approved: any[];
    completed: any[];
  };
}

interface MessageTemplate {
  id: string;
  name: string;
  category: string;
  template: string;
  variables: string[];
}

const HealthcarePanel: React.FC<HealthcarePanelProps> = ({
  contactId,
  contactName,
  contactPhone,
  contactEmail,
  onSendMessage,
  onSendMediaUrl,
  onClose,
  open,
  initialTab = 0,
  openScheduleOnMount = false,
  onPatientLinked,
}) => {
  // States
  const [loading, setLoading] = useState(false);
  const [linkedPatient, setLinkedPatient] = useState<Patient | null>(null);
  const [summary, setSummary] = useState<HealthcareSummary | null>(null);
  const [autoMatches, setAutoMatches] = useState<any[]>([]);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [showLinkSection, setShowLinkSection] = useState(false);
  const [showNewPatientForm, setShowNewPatientForm] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [showTemplatesDialog, setShowTemplatesDialog] = useState(false);
  const [messageTemplates, setMessageTemplates] = useState<MessageTemplate[]>([]);
  const [professionals, setProfessionals] = useState<any[]>([]);
  const [daySchedule, setDaySchedule] = useState<any[]>([]);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    info: true, alerts: true, appointments: true, history: false, financial: false, documents: false,
  });

  // Quick schedule form
  const [scheduleForm, setScheduleForm] = useState({
    appointment_date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    duration_minutes: 30,
    professional_id: '',
    appointment_type: 'consultation',
    billing_mode: 'particular' as 'particular' | 'plano',
    title: '',
    notes: '',
  });

  // New patient form
  const [newPatientForm, setNewPatientForm] = useState({
    full_name: contactName || '',
    cpf: '',
    birth_date: '',
    gender: '',
    email: contactEmail || '',
  });

  const [snackMessage, setSnackMessage] = useState('');
  const [snackSeverity, setSnackSeverity] = useState<'success' | 'error'>('success');

  // Send document dialog
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendDocData, setSendDocData] = useState<any>(null);

  // Evolution expansion
  const [expandedEvolution, setExpandedEvolution] = useState<string | null>(null);
  const [evolutionDialogOpen, setEvolutionDialogOpen] = useState(false);

  /** Orçamento / receituário / exame — linha expandida no histórico (tab Saúde) */
  const [expandedHistoryRow, setExpandedHistoryRow] = useState<string | null>(null);

  // Documents state
  const [documents, setDocuments] = useState<any[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [showDocUpload, setShowDocUpload] = useState(false);
  const [docUploadForm, setDocUploadForm] = useState({ title: '', document_type: 'contract', description: '' });
  const [docFile, setDocFile] = useState<File | null>(null);
  const [docUploading, setDocUploading] = useState(false);
  const docFileInputRef = React.useRef<HTMLInputElement>(null);

  // Images (gallery) state
  const [patientImages, setPatientImages] = useState<any[]>([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);

  // Load data when panel opens
  useEffect(() => {
    if (open && contactId) {
      setActiveTab(initialTab);
      loadPatientData();
      loadProfessionals();
      loadTemplates();
    }
  }, [open, contactId, initialTab]);

  useEffect(() => {
    setExpandedHistoryRow(null);
  }, [linkedPatient?.id]);

  useEffect(() => {
    if (!open || !openScheduleOnMount || !linkedPatient) return;
    setScheduleForm((f) => ({
      ...f,
      title: `Consulta - ${linkedPatient.full_name}`,
      appointment_date: new Date().toISOString().split('T')[0],
    }));
    void loadDaySchedule(new Date().toISOString().split('T')[0]);
    setShowScheduleDialog(true);
  }, [open, openScheduleOnMount, linkedPatient?.id, linkedPatient?.full_name]);

  const loadPatientData = useCallback(async () => {
    if (!contactId) return;
    setLoading(true);
    try {
      // Buscar paciente vinculado
      const { data: linkedRes } = await axios.get(getApiUrl(`/clinic/healthcare-panel/contact/${contactId}/patient`));
      if (linkedRes.success && linkedRes.data) {
        setLinkedPatient(linkedRes.data);
        // Carregar resumo de saúde
        await loadHealthcareSummary(linkedRes.data.id);
      } else {
        setLinkedPatient(null);
        setSummary(null);
        // Tentar auto-match
        await loadAutoMatches();
      }
    } catch (err) {
      console.error('Error loading patient data:', err);
      setLinkedPatient(null);
      await loadAutoMatches();
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  const loadHealthcareSummary = async (patientId: string) => {
    try {
      const { data: res } = await axios.get(getApiUrl(`/clinic/healthcare-panel/patient/${patientId}/summary`));
      if (res.success) {
        setSummary(res.data);
      }
    } catch (err) {
      console.error('Error loading healthcare summary:', err);
    }
  };

  const loadAutoMatches = async () => {
    if (!contactId) return;
    try {
      const { data: res } = await axios.get(getApiUrl(`/clinic/healthcare-panel/contact/${contactId}/auto-match`));
      if (res.success && res.data?.matches) {
        setAutoMatches(res.data.matches);
      }
    } catch (err) {
      console.error('Error loading auto-matches:', err);
    }
  };

  const loadProfessionals = async () => {
    try {
      const { data: res } = await axios.get(getApiUrl('/clinic/professionals'));
      if (res.data) {
        setProfessionals(Array.isArray(res.data) ? res.data : res.data.data || []);
      }
    } catch (err) {
      console.error('Error loading professionals:', err);
    }
  };

  const loadTemplates = async () => {
    try {
      const { data: res } = await axios.get(getApiUrl('/clinic/healthcare-panel/message-templates'));
      if (res.success) {
        setMessageTemplates(res.data);
      }
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  };

  const loadDocuments = useCallback(async (patientId: string) => {
    setDocsLoading(true);
    try {
      const { data: res } = await axios.get(getApiUrl(`/clinic/patients/${patientId}/documents`));
      if (res.success) setDocuments(res.data);
    } catch (err) {
      console.error('Error loading documents:', err);
    } finally {
      setDocsLoading(false);
    }
  }, []);

  const handleDocUpload = async () => {
    if (!linkedPatient || !docFile) return;
    setDocUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', docFile);
      formData.append('title', docUploadForm.title || docFile.name);
      formData.append('document_type', docUploadForm.document_type);
      if (docUploadForm.description) formData.append('description', docUploadForm.description);

      const { data: res } = await axios.post(
        getApiUrl(`/clinic/patients/${linkedPatient.id}/documents/upload`),
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (res.success) {
        setDocuments(prev => [res.data, ...prev]);
        setShowDocUpload(false);
        setDocFile(null);
        setDocUploadForm({ title: '', document_type: 'contract', description: '' });
        setSnackSeverity('success');
        setSnackMessage('Documento anexado com sucesso!');
      }
    } catch (err: any) {
      setSnackSeverity('error');
      setSnackMessage('Erro ao fazer upload do documento');
    } finally {
      setDocUploading(false);
    }
  };

  const getDocTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      contract: 'Contrato', id_document: 'Documento de Identidade', insurance_card: 'Carteirinha',
      exam_result: 'Resultado de Exame', xray: 'Raio-X / Imagem', photo: 'Foto',
      consent_form: 'Termo de Consentimento', other: 'Outro',
    };
    return labels[type] || type;
  };

  const getDocTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      contract: '#2563eb', id_document: '#7c3aed', insurance_card: '#059669',
      exam_result: '#d97706', xray: '#475569', photo: '#ec4899',
      consent_form: '#14B8A6', other: '#6b7280',
    };
    return colors[type] || '#6b7280';
  };

  const loadPatientImages = useCallback(async (patientId: string) => {
    setImagesLoading(true);
    try {
      const { data: res } = await axios.get(getApiUrl('/clinic/patient-images'), { params: { patient_id: patientId, limit: 50 } });
      if (res.success) setPatientImages(res.data || []);
    } catch (err) {
      console.error('Error loading patient images:', err);
    } finally {
      setImagesLoading(false);
    }
  }, []);

  // Load documents when switching to documents tab (tab 5); imagens tab 6
  useEffect(() => {
    if (activeTab === 5 && linkedPatient?.id) {
      loadDocuments(linkedPatient.id);
    }
    if (activeTab === 6 && linkedPatient?.id) {
      loadPatientImages(linkedPatient.id);
    }
  }, [activeTab, linkedPatient?.id, loadDocuments, loadPatientImages]);

  const loadDaySchedule = async (date: string) => {
    try {
      const { data: res } = await axios.get(getApiUrl('/clinic/healthcare-panel/day-schedule'), {
        params: { date }
      });
      if (res.success) {
        setDaySchedule(res.data);
      }
    } catch (err) {
      console.error('Error loading day schedule:', err);
    }
  };

  // Actions
  const handleLinkPatient = async (patientId: string) => {
    if (!contactId) return;
    try {
      const { data: res } = await axios.post(getApiUrl('/clinic/healthcare-panel/link'), {
        contact_id: contactId,
        patient_id: patientId,
      });
      if (res.success) {
        setSnackSeverity('success');
        setSnackMessage('Paciente vinculado com sucesso!');
        setShowLinkSection(false);
        setAutoMatches([]);
        await loadPatientData();
        onPatientLinked?.();
      }
    } catch (err: any) {
      console.error('Error linking patient:', err);
      setSnackSeverity('error');
      setSnackMessage('Erro ao vincular paciente');
    }
  };

  const handleUnlinkPatient = async () => {
    if (!contactId) return;
    try {
      await axios.post(getApiUrl('/clinic/healthcare-panel/unlink'), { contact_id: contactId });
      setLinkedPatient(null);
      setSummary(null);
      setSnackSeverity('success');
      setSnackMessage('Paciente desvinculado');
      onPatientLinked?.();
      await loadAutoMatches();
    } catch (err) {
      console.error('Error unlinking patient:', err);
    }
  };

  const handleCreatePatientFromContact = async () => {
    if (!contactId) return;
    try {
      const { data: res } = await axios.post(getApiUrl('/clinic/healthcare-panel/create-patient-from-contact'), {
        contact_id: contactId,
        ...newPatientForm,
      });
      if (res.success) {
        setSnackSeverity('success');
        setSnackMessage('Paciente criado e vinculado com sucesso!');
        setShowNewPatientForm(false);
        await loadPatientData();
        onPatientLinked?.();
      }
    } catch (err: any) {
      console.error('Error creating patient:', err);
      setSnackSeverity('error');
      setSnackMessage('Erro ao criar paciente: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleQuickSchedule = async () => {
    if (!linkedPatient) return;
    try {
      const { data: res } = await axios.post(getApiUrl('/clinic/healthcare-panel/quick-schedule'), {
        patient_id: linkedPatient.id,
        ...scheduleForm,
        professional_id: scheduleForm.professional_id || undefined,
      });
      if (res.success) {
        setSnackSeverity('success');
        setSnackMessage('Consulta agendada com sucesso!');
        setShowScheduleDialog(false);
        await loadHealthcareSummary(linkedPatient.id);
      }
    } catch (err: any) {
      console.error('Error scheduling:', err);
      setSnackSeverity('error');
      setSnackMessage('Erro ao agendar: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSearchPatients = async (query: string) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const { data: res } = await axios.get(getApiUrl('/clinic/healthcare-panel/search-patients'), {
        params: { search: query }
      });
      if (res.success) {
        setSearchResults(res.data);
      }
    } catch (err) {
      console.error('Error searching patients:', err);
    } finally {
      setSearching(false);
    }
  };

  const handleSendTemplate = (template: MessageTemplate) => {
    if (!onSendMessage) return;
    let message = template.template;
    const patientName = linkedPatient?.full_name || contactName || 'paciente';
    message = message.replace('{patient_name}', patientName);

    // Replace other variables with placeholders
    if (summary?.upcoming_appointments?.[0]) {
      const apt = summary.upcoming_appointments[0];
      message = message.replace('{date}', formatDate(apt.appointment_date));
      message = message.replace('{time}', apt.start_time?.substring(0, 5) || '');
      message = message.replace('{professional_name}', apt.clinic_professionals?.full_name || 'o profissional');
    } else {
      message = message.replace('{date}', '[data]');
      message = message.replace('{time}', '[hora]');
      message = message.replace('{professional_name}', '[profissional]');
    }

    message = message.replace('{budget_number}', '[nº orçamento]');
    message = message.replace('{total}', '[valor]');
    message = message.replace('{amount}', '[valor]');

    onSendMessage(message);
    setShowTemplatesDialog(false);
    setSnackSeverity('success');
    setSnackMessage('Mensagem inserida no campo de texto');
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Helpers
  const formatDate = (date: string) => {
    if (!date) return '-';
    try {
      return new Date(date + 'T12:00:00').toLocaleDateString('pt-BR');
    } catch { return date; }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const getAge = (birthDate: string) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate + 'T12:00:00');
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { label: string; color: string; bg: string }> = {
      scheduled: { label: 'Agendada', color: '#2563eb', bg: '#dbeafe' },
      confirmed: { label: 'Confirmada', color: '#059669', bg: '#d1fae5' },
      waiting: { label: 'Na Recepção', color: '#d97706', bg: '#fef3c7' },
      in_progress: { label: 'Em Atendimento', color: '#7c3aed', bg: '#ede9fe' },
      completed: { label: 'Concluída', color: '#14B8A6', bg: '#ccfbf1' },
      cancelled: { label: 'Cancelada', color: '#dc2626', bg: '#fee2e2' },
      no_show: { label: 'Faltou', color: '#9ca3af', bg: '#f3f4f6' },
      draft: { label: 'Rascunho', color: '#6b7280', bg: '#f3f4f6' },
      sent: { label: 'Enviado', color: '#2563eb', bg: '#dbeafe' },
      approved: { label: 'Aprovado', color: '#059669', bg: '#d1fae5' },
      rejected: { label: 'Rejeitado', color: '#dc2626', bg: '#fee2e2' },
      active: { label: 'Ativo', color: '#14B8A6', bg: '#ccfbf1' },
      in_treatment: { label: 'Em Tratamento', color: '#7c3aed', bg: '#ede9fe' },
    };
    return configs[status] || { label: status, color: '#6b7280', bg: '#f3f4f6' };
  };

  const getGenderLabel = (gender: string) => {
    const labels: Record<string, string> = {
      masculine: 'Masculino', feminine: 'Feminino', other: 'Outro', prefer_not_say: 'Não informado',
    };
    return labels[gender] || gender || '-';
  };

  if (!open) return null;

  // =============================================
  // RENDER
  // =============================================

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
          bgcolor: '#f8fffe',
        }
      }}
    >
      {/* Snack Message */}
      {snackMessage && (
        <Alert
          severity={snackSeverity}
          onClose={() => { setSnackMessage(''); setSnackSeverity('success'); }}
          sx={{ position: 'absolute', top: 8, left: 8, right: 8, zIndex: 10, borderRadius: 2 }}
        >
          {snackMessage}
        </Alert>
      )}

      {/* Header */}
      <DialogTitle sx={{ pb: 1, borderBottom: '1px solid rgba(20,184,166,0.12)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {linkedPatient ? (
            <PatientAvatarUpload
              patientId={linkedPatient.id}
              currentAvatarUrl={linkedPatient.avatar_url}
              patientName={linkedPatient.full_name}
              size={52}
              onAvatarUpdated={(newUrl) => {
                setLinkedPatient(prev => prev ? { ...prev, avatar_url: newUrl } : prev);
              }}
            />
          ) : (
            <Avatar
              sx={{
                bgcolor: '#94a3b8',
                width: 52, height: 52, fontSize: '1.3rem', fontWeight: 700,
              }}
            >
              {(contactName || '?').charAt(0).toUpperCase()}
            </Avatar>
          )}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
              {linkedPatient?.full_name || contactName || 'Contato'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              {linkedPatient ? (
                <>
                  <Chip
                    icon={<HospitalIcon sx={{ fontSize: 14 }} />}
                    label="Paciente Vinculado"
                    size="small"
                    sx={{ bgcolor: '#d1fae5', color: '#059669', fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                  />
                  {linkedPatient.blood_type && (
                    <Chip label={linkedPatient.blood_type} size="small"
                      sx={{ bgcolor: '#fee2e2', color: '#dc2626', fontWeight: 700, fontSize: '0.7rem', height: 24 }} />
                  )}
                  {linkedPatient.birth_date && getAge(linkedPatient.birth_date) !== null && (
                    <Typography variant="caption" color="text.secondary">
                      {getAge(linkedPatient.birth_date)} anos
                    </Typography>
                  )}
                </>
              ) : (
                <Chip
                  icon={<WhatsAppIcon sx={{ fontSize: 14 }} />}
                  label="Contato WhatsApp"
                  size="small"
                  sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                />
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Atualizar dados">
              <IconButton size="small" onClick={loadPatientData} sx={{ color: '#14B8A6' }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#14B8A6' }} />
          </Box>
        ) : linkedPatient ? (
          /* ============================================
             PACIENTE VINCULADO - PAINEL COMPLETO
             ============================================ */
          <Box>
            {/* Quick Actions Bar */}
            <Box sx={{
              display: 'flex', gap: 1, p: 2, bgcolor: 'white',
              borderBottom: '1px solid rgba(20,184,166,0.08)',
              flexWrap: 'wrap',
            }}>
              <Button
                size="small" variant="contained" startIcon={<CalendarIcon />}
                onClick={() => {
                  setScheduleForm(f => ({
                    ...f, title: `Consulta - ${linkedPatient.full_name}`,
                    appointment_date: new Date().toISOString().split('T')[0],
                  }));
                  loadDaySchedule(new Date().toISOString().split('T')[0]);
                  setShowScheduleDialog(true);
                }}
                sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', borderRadius: 2, fontSize: '0.75rem', fontWeight: 600 }}
              >
                Agendar
              </Button>
              <Button
                size="small" variant="outlined" startIcon={<SendIcon />}
                onClick={() => setShowTemplatesDialog(true)}
                sx={{ borderColor: '#14B8A6', color: '#14B8A6', textTransform: 'none', borderRadius: 2, fontSize: '0.75rem', fontWeight: 600, '&:hover': { borderColor: '#0d9488', bgcolor: 'rgba(20,184,166,0.04)' } }}
              >
                Msg. Saúde
              </Button>
              <Button
                size="small" variant="outlined" startIcon={<ViewIcon />}
                onClick={() => window.open(`/clinic/patients?id=${linkedPatient.id}`, '_blank')}
                sx={{ borderColor: '#64748b', color: '#64748b', textTransform: 'none', borderRadius: 2, fontSize: '0.75rem', fontWeight: 600, '&:hover': { borderColor: '#475569', bgcolor: 'rgba(100,116,139,0.04)' } }}
              >
                Prontuário
              </Button>
              <Button
                size="small" variant="outlined" startIcon={<DocIcon />}
                onClick={() => window.open(`/clinic/budgets?patient=${linkedPatient.id}`, '_blank')}
                sx={{ borderColor: '#64748b', color: '#64748b', textTransform: 'none', borderRadius: 2, fontSize: '0.75rem', fontWeight: 600, '&:hover': { borderColor: '#475569', bgcolor: 'rgba(100,116,139,0.04)' } }}
              >
                Orçamento
              </Button>
              <Button
                size="small" variant="outlined" startIcon={<MedicationIcon />}
                onClick={() => window.open(`/clinic/prescriptions`, '_blank')}
                sx={{ borderColor: '#64748b', color: '#64748b', textTransform: 'none', borderRadius: 2, fontSize: '0.75rem', fontWeight: 600, '&:hover': { borderColor: '#475569', bgcolor: 'rgba(100,116,139,0.04)' } }}
              >
                Receituário
              </Button>
            </Box>

            {/* Tabs */}
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{
                px: 2, bgcolor: 'white', borderBottom: '1px solid rgba(20,184,166,0.08)',
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', minHeight: 44, py: 0 },
                '& .Mui-selected': { color: '#14B8A6 !important' },
                '& .MuiTabs-indicator': { bgcolor: '#14B8A6' },
              }}
            >
              <Tab label="Perfil" icon={<PersonIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
              <Tab label={
                <Badge badgeContent={summary?.upcoming_appointments?.length || 0} color="primary" sx={{ '& .MuiBadge-badge': { bgcolor: '#14B8A6', fontSize: '0.65rem' } }}>
                  Agenda
                </Badge>
              } icon={<CalendarIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
              <Tab label="Histórico" icon={<AssignmentIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
              <Tab label={
                <Badge
                  badgeContent={summary?.stats?.total_procedure_items ?? (
                    (summary?.patient_procedures?.pending?.length || 0) +
                    (summary?.patient_procedures?.approved?.length || 0) +
                    (summary?.patient_procedures?.completed?.length || 0)
                  )}
                  color="primary"
                  sx={{ '& .MuiBadge-badge': { bgcolor: '#0d9488', fontSize: '0.65rem' } }}
                >
                  Procedimentos
                </Badge>
              } icon={<MedicalIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
              <Tab label="Financeiro" icon={<MoneyIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
              <Tab label={
                <Badge badgeContent={documents.length || 0} color="primary" sx={{ '& .MuiBadge-badge': { bgcolor: '#2563eb', fontSize: '0.65rem' } }}>
                  Docs
                </Badge>
              } icon={<FolderIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
              <Tab label={
                <Badge badgeContent={patientImages.length || 0} color="primary" sx={{ '& .MuiBadge-badge': { bgcolor: '#14B8A6', fontSize: '0.65rem' } }}>
                  Imagens
                </Badge>
              } icon={<ImageIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
            </Tabs>

            {/* TAB 0 - PERFIL */}
            {activeTab === 0 && (
              <Box sx={{ p: 2 }}>
                {/* Alertas de Saúde */}
                {(linkedPatient.allergies?.length || linkedPatient.chronic_conditions?.length || linkedPatient.medications?.length) ? (
                  <Card sx={{ mb: 2, border: '1px solid #fbbf24', borderRadius: 2, bgcolor: '#fffbeb' }}>
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <WarningIcon sx={{ color: '#d97706', fontSize: 18 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#92400e' }}>
                          Alertas de Saúde
                        </Typography>
                      </Box>
                      {linkedPatient.allergies && linkedPatient.allergies.length > 0 && (
                        <Box sx={{ mb: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#dc2626' }}>
                            Alergias:
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
                            {linkedPatient.allergies.map((a, i) => (
                              <Chip key={i} label={a} size="small"
                                sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontSize: '0.65rem', height: 20, fontWeight: 600 }} />
                            ))}
                          </Box>
                        </Box>
                      )}
                      {linkedPatient.chronic_conditions && linkedPatient.chronic_conditions.length > 0 && (
                        <Box sx={{ mb: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#d97706' }}>
                            Condições Crônicas:
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
                            {linkedPatient.chronic_conditions.map((c, i) => (
                              <Chip key={i} label={c} size="small"
                                sx={{ bgcolor: '#fef3c7', color: '#92400e', fontSize: '0.65rem', height: 20, fontWeight: 600 }} />
                            ))}
                          </Box>
                        </Box>
                      )}
                      {linkedPatient.medications && linkedPatient.medications.length > 0 && (
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#2563eb' }}>
                            Medicamentos em Uso:
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
                            {linkedPatient.medications.map((m, i) => (
                              <Chip key={i} label={m} size="small"
                                sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontSize: '0.65rem', height: 20, fontWeight: 600 }} />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ) : null}

                {/* Informações Pessoais */}
                <Card sx={{ mb: 2, borderRadius: 2, border: '1px solid rgba(20,184,166,0.1)' }}>
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1.5 }}>
                      Dados do Paciente
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PersonIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                          <Typography variant="caption" color="text.secondary">Nome</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{linkedPatient.full_name}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                          <Typography variant="caption" color="text.secondary">Telefone</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{linkedPatient.phone || linkedPatient.whatsapp || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <EmailIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                          <Typography variant="caption" color="text.secondary">Email</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{linkedPatient.email || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CakeIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                          <Typography variant="caption" color="text.secondary">Nascimento</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                          {linkedPatient.birth_date ? `${formatDate(linkedPatient.birth_date)} (${getAge(linkedPatient.birth_date)} anos)` : '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">CPF</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', fontFamily: 'monospace' }}>{linkedPatient.cpf || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">Sexo</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{getGenderLabel(linkedPatient.gender || '')}</Typography>
                      </Grid>
                      {linkedPatient.insurance_plan && (
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">Convênio</Typography>
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                            {linkedPatient.insurance_plan} {linkedPatient.insurance_number ? `- ${linkedPatient.insurance_number}` : ''}
                          </Typography>
                        </Grid>
                      )}
                      {linkedPatient.emergency_contact_name && (
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <WarningIcon sx={{ fontSize: 14, color: '#dc2626' }} />
                            <Typography variant="caption" color="text.secondary">Contato de Emergência</Typography>
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                            {linkedPatient.emergency_contact_name} - {linkedPatient.emergency_contact_phone}
                          </Typography>
                        </Grid>
                      )}
                      {linkedPatient.medical_notes && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">Observações Médicas</Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', bgcolor: '#f1f5f9', p: 1, borderRadius: 1, mt: 0.5 }}>
                            {linkedPatient.medical_notes}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>

                {/* KPIs Rápidos */}
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  <Grid item xs={3}>
                    <Card sx={{ borderRadius: 2, border: '1px solid rgba(20,184,166,0.1)', textAlign: 'center' }}>
                      <CardContent sx={{ py: 1, px: 1, '&:last-child': { pb: 1 } }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#14B8A6' }}>{linkedPatient.total_visits || 0}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>Consultas</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={3}>
                    <Card sx={{ borderRadius: 2, border: '1px solid rgba(20,184,166,0.1)', textAlign: 'center' }}>
                      <CardContent sx={{ py: 1, px: 1, '&:last-child': { pb: 1 } }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#2563eb' }}>{summary?.stats?.total_budgets || 0}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>Orçamentos</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={3}>
                    <Card sx={{ borderRadius: 2, border: '1px solid rgba(20,184,166,0.1)', textAlign: 'center' }}>
                      <CardContent sx={{ py: 1, px: 1, '&:last-child': { pb: 1 } }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#7c3aed' }}>{summary?.stats?.total_prescriptions || 0}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>Receitas</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={3}>
                    <Card sx={{ borderRadius: 2, border: '1px solid rgba(20,184,166,0.1)', textAlign: 'center' }}>
                      <CardContent sx={{ py: 1, px: 1, '&:last-child': { pb: 1 } }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#059669' }}>
                          {formatCurrency(linkedPatient.total_spent || 0).replace('R$', '').trim().split(',')[0]}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>Investido</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Tags */}
                {linkedPatient.tags && linkedPatient.tags.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Tags</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {linkedPatient.tags.map((tag, i) => (
                        <Chip key={i} label={tag} size="small"
                          sx={{ bgcolor: 'rgba(20,184,166,0.1)', color: '#0d9488', fontSize: '0.65rem', height: 22 }} />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Desvincular */}
                <Button
                  size="small" variant="text" startIcon={<LinkOffIcon />}
                  onClick={handleUnlinkPatient}
                  sx={{ color: '#94a3b8', textTransform: 'none', fontSize: '0.75rem', '&:hover': { color: '#dc2626', bgcolor: 'rgba(220,38,38,0.05)' } }}
                >
                  Desvincular paciente
                </Button>
              </Box>
            )}

            {/* TAB 1 - AGENDA */}
            {activeTab === 1 && (
              <Box sx={{ p: 2 }}>
                {/* Próximas consultas */}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EventIcon sx={{ color: '#14B8A6', fontSize: 18 }} />
                  Próximas Consultas
                </Typography>
                {summary?.upcoming_appointments && summary.upcoming_appointments.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                    {summary.upcoming_appointments.map((apt: any) => {
                      const st = getStatusConfig(apt.status);
                      return (
                        <Card key={apt.id} sx={{ borderRadius: 2, border: '1px solid rgba(20,184,166,0.12)', '&:hover': { boxShadow: '0 2px 8px rgba(20,184,166,0.12)' } }}>
                          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarIcon sx={{ fontSize: 16, color: '#14B8A6' }} />
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {formatDate(apt.appointment_date)}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#14B8A6', fontWeight: 600 }}>
                                  {apt.start_time?.substring(0, 5)}
                                </Typography>
                              </Box>
                              <Chip label={st.label} size="small"
                                sx={{ bgcolor: st.bg, color: st.color, fontSize: '0.65rem', height: 22, fontWeight: 600 }} />
                            </Box>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#475569' }}>
                              {apt.title || apt.appointment_type}
                            </Typography>
                            {apt.clinic_professionals && (
                              <Typography variant="caption" color="text.secondary">
                                Dr(a). {apt.clinic_professionals.full_name} {apt.clinic_professionals.specialty ? `- ${apt.clinic_professionals.specialty}` : ''}
                              </Typography>
                            )}
                            <Box
                              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 1, pt: 0.5, borderTop: '1px dashed rgba(148,163,184,0.35)' }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                                Teleconsulta
                              </Typography>
                              <TeleconsultButton appointmentId={apt.id} appointmentStatus={apt.status} compact />
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                ) : (
                  <Alert severity="info" sx={{ mb: 3, borderRadius: 2, '& .MuiAlert-icon': { color: '#14B8A6' } }}>
                    Nenhuma consulta agendada. Clique em "Agendar" para marcar uma consulta.
                  </Alert>
                )}

                {/* Consultas Recentes */}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TimeIcon sx={{ color: '#64748b', fontSize: 18 }} />
                  Consultas Recentes
                </Typography>
                {summary?.recent_appointments && summary.recent_appointments.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {summary.recent_appointments.slice(0, 5).map((apt: any) => {
                      const st = getStatusConfig(apt.status);
                      return (
                        <Box key={apt.id} sx={{
                          display: 'flex', alignItems: 'center', gap: 1.5, py: 0.8, px: 1.5,
                          borderRadius: 1.5, bgcolor: 'white', border: '1px solid #f1f5f9',
                        }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: st.color, flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, minWidth: 70 }}>
                            {formatDate(apt.appointment_date)}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#14B8A6', fontWeight: 600, minWidth: 40 }}>
                            {apt.start_time?.substring(0, 5)}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', flex: 1, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {apt.title || apt.appointment_type}
                          </Typography>
                          <Chip label={st.label} size="small"
                            sx={{ bgcolor: st.bg, color: st.color, fontSize: '0.6rem', height: 18, fontWeight: 600 }} />
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    Nenhuma consulta registrada
                  </Typography>
                )}
              </Box>
            )}

            {/* TAB 2 - HISTÓRICO */}
            {activeTab === 2 && (
              <Box sx={{ p: 2 }}>
                {/* Evoluções Clínicas */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <NoteAltIcon sx={{ color: '#14B8A6', fontSize: 18 }} />
                      Evoluções Clínicas
                    </Typography>
                    {linkedPatient && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setEvolutionDialogOpen(true)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          bgcolor: '#14B8A6',
                          '&:hover': { bgcolor: '#0d9488' },
                          borderRadius: 2,
                        }}
                      >
                        Nova evolução
                      </Button>
                    )}
                  </Box>
                  {summary?.recent_evolutions && summary.recent_evolutions.length > 0 ? (
                    summary.recent_evolutions.map((evo: any) => (
                      <Box key={evo.id} sx={{ mb: 0.5 }}>
                        <Box sx={{
                          display: 'flex', alignItems: 'center', gap: 1, py: 0.8, px: 1.5,
                          borderRadius: expandedEvolution === evo.id ? '8px 8px 0 0' : 1.5,
                          bgcolor: 'white', border: '1px solid rgba(20,184,166,0.15)',
                          cursor: 'pointer',
                          '&:hover': { borderColor: '#14B8A6' },
                        }}
                          onClick={() => setExpandedEvolution(expandedEvolution === evo.id ? null : evo.id)}
                        >
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: evo.is_signed ? '#059669' : '#f59e0b', flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#14B8A6', minWidth: 70 }}>
                            {formatDate(evo.evolution_date)}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', flex: 1, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {resolveEvolutionEntryFormat(evo) === 'free_text'
                              ? (evo.evolution_free_text ? clinicalHtmlToPlainPreview(evo.evolution_free_text, 120) : 'Evolução clínica')
                              : (evo.assessment || evo.subjective || 'Evolução clínica')}
                          </Typography>
                          {evo.professional && (
                            <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem', flexShrink: 0 }}>
                              Dr(a). {evo.professional.full_name?.split(' ')[0]}
                            </Typography>
                          )}
                          <Tooltip title="Enviar">
                            <IconButton size="small" onClick={(e) => {
                              e.stopPropagation();
                              setSendDocData({ id: evo.id, patientId: linkedPatient?.id, type: 'evolution', label: 'Evolução Clínica' });
                              setSendDialogOpen(true);
                            }} sx={{ color: '#3b82f6', p: 0.3 }}>
                              <ShareIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                          <ExpandMoreIcon2 sx={{ fontSize: 16, color: '#94a3b8', transform: expandedEvolution === evo.id ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
                        </Box>
                        <Collapse in={expandedEvolution === evo.id}>
                          <Box sx={{ p: 1.5, bgcolor: '#f8fffe', border: '1px solid rgba(20,184,166,0.15)', borderTop: 0, borderRadius: '0 0 8px 8px' }}>
                            {resolveEvolutionEntryFormat(evo) === 'free_text' ? (
                              evo.evolution_free_text && (
                                <Box sx={{ mb: 1 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#0d9488' }}>Texto livre</Typography>
                                  <ClinicalHtmlField
                                    html={evo.evolution_free_text}
                                    sx={{ fontSize: '0.78rem', color: '#334155', mt: 0.25 }}
                                  />
                                </Box>
                              )
                            ) : (
                              <>
                                {evo.subjective && (
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#0d9488' }}>Subjetivo (S):</Typography>
                                    <Typography variant="body2" sx={{ fontSize: '0.78rem', color: '#334155' }}>{evo.subjective}</Typography>
                                  </Box>
                                )}
                                {evo.objective && (
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#0d9488' }}>Objetivo (O):</Typography>
                                    <Typography variant="body2" sx={{ fontSize: '0.78rem', color: '#334155' }}>{evo.objective}</Typography>
                                  </Box>
                                )}
                                {evo.assessment && (
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#0d9488' }}>Avaliação (A):</Typography>
                                    <Typography variant="body2" sx={{ fontSize: '0.78rem', color: '#334155' }}>{evo.assessment}</Typography>
                                  </Box>
                                )}
                                {evo.plan && (
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#0d9488' }}>Plano (P):</Typography>
                                    <Typography variant="body2" sx={{ fontSize: '0.78rem', color: '#334155' }}>{evo.plan}</Typography>
                                  </Box>
                                )}
                              </>
                            )}
                            {evo.diagnosis && (
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#7c3aed' }}>Diagnóstico:</Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.78rem', color: '#334155' }}>
                                  {evo.diagnosis} {evo.diagnosis_code ? `(${evo.diagnosis_code})` : ''}
                                </Typography>
                              </Box>
                            )}
                            {evo.vital_signs && Object.keys(evo.vital_signs).some((k: string) => evo.vital_signs[k]) && (
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                {evo.vital_signs.blood_pressure && <Chip label={`PA: ${evo.vital_signs.blood_pressure}`} size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#fee2e2', color: '#991b1b' }} />}
                                {evo.vital_signs.heart_rate && <Chip label={`FC: ${evo.vital_signs.heart_rate}`} size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#fce7f3', color: '#9d174d' }} />}
                                {evo.vital_signs.temperature && <Chip label={`T: ${evo.vital_signs.temperature}°C`} size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#fef3c7', color: '#92400e' }} />}
                                {evo.vital_signs.weight && <Chip label={`Peso: ${evo.vital_signs.weight}kg`} size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#dbeafe', color: '#1e40af' }} />}
                              </Box>
                            )}
                            {evo.professional && (
                              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#94a3b8', fontSize: '0.65rem' }}>
                                Dr(a). {evo.professional.full_name} {evo.professional.specialty ? `- ${evo.professional.specialty}` : ''}
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1, fontSize: '0.8rem' }}>Nenhuma evolução registrada</Typography>
                  )}
                </Box>

                {/* Orçamentos */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DocIcon sx={{ color: '#2563eb', fontSize: 18 }} />
                    Orçamentos Recentes
                  </Typography>
                  {summary?.recent_budgets && summary.recent_budgets.length > 0 ? (
                    summary.recent_budgets.map((b: any) => {
                      const st = getStatusConfig(b.status);
                      const prog = b.progress;
                      const rowKey = `bud:${b.id}`;
                      const isOpen = expandedHistoryRow === rowKey;
                      const bi = (b.items || []).slice().sort((a: any, x: any) => (a.sort_order ?? 0) - (x.sort_order ?? 0));
                      return (
                        <Box key={b.id} sx={{ py: 0.8, px: 1.5, borderRadius: 1.5, bgcolor: 'white', border: '1px solid #f1f5f9', mb: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#2563eb', minWidth: 80 }}>
                              {b.budget_number}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', minWidth: 70 }}>{formatDate(b.issue_date)}</Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', flex: 1, fontWeight: 600 }}>{formatCurrency(b.total)}</Typography>
                            <Chip label={st.label} size="small" sx={{ bgcolor: st.bg, color: st.color, fontSize: '0.6rem', height: 18, fontWeight: 600 }} />
                            <Tooltip title={isOpen ? 'Recolher' : 'Ver itens'}>
                              <IconButton size="small" onClick={() => setExpandedHistoryRow(isOpen ? null : rowKey)} sx={{ color: '#64748b', p: 0.3 }}>
                                {isOpen ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Enviar">
                              <IconButton size="small" onClick={() => {
                                setSendDocData({ id: b.id, patientId: linkedPatient?.id, type: 'budget', label: 'Orçamento' });
                                setSendDialogOpen(true);
                              }} sx={{ color: '#3b82f6', p: 0.3 }}>
                                <ShareIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          {prog?.has_plan && prog.total_items > 0 && (
                            <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: '#e2e8f0', overflow: 'hidden' }}>
                                <Box sx={{ height: '100%', borderRadius: 2, bgcolor: prog.pending_items === 0 ? '#16a34a' : '#0d9488', width: `${(prog.completed_items / prog.total_items) * 100}%`, transition: 'width 0.3s' }} />
                              </Box>
                              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#64748b', whiteSpace: 'nowrap' }}>
                                {prog.completed_items}/{prog.total_items}
                              </Typography>
                            </Box>
                          )}
                          <Collapse in={isOpen}>
                            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #f1f5f9', bgcolor: '#f8fafc', mx: -1.5, px: 1.5, pb: 1, borderRadius: '0 0 8px 8px' }}>
                              {b.validity_date && (
                                <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mb: 0.5 }}>Válido até {formatDate(b.validity_date)}</Typography>
                              )}
                              {b.notes && (
                                <Typography variant="caption" sx={{ display: 'block', color: '#475569', mb: 0.75, whiteSpace: 'pre-wrap' }}>Obs.: {b.notes}</Typography>
                              )}
                              {bi.length === 0 ? (
                                <Typography variant="caption" color="text.secondary">Nenhum item listado neste resumo.</Typography>
                              ) : (
                                bi.map((it: any, idx: number) => (
                                  <Box key={it.id || idx} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'baseline', py: 0.35, borderBottom: idx < bi.length - 1 ? '1px dashed #e2e8f0' : 'none' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af', flex: '1 1 100%' }}>{it.procedure_name || 'Procedimento'}</Typography>
                                    {it.procedure_description && (
                                      <Typography variant="caption" sx={{ display: 'block', color: '#64748b', flex: '1 1 100%', fontSize: '0.65rem' }}>{it.procedure_description}</Typography>
                                    )}
                                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                                      Qtd {it.quantity ?? 1} × {formatCurrency(it.unit_price ?? 0)} → <strong>{formatCurrency(it.total ?? 0)}</strong>
                                    </Typography>
                                  </Box>
                                ))
                              )}
                              <Button size="small" endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />} onClick={() => window.open('/clinic/budgets', '_blank')}
                                sx={{ mt: 1, textTransform: 'none', fontSize: '0.7rem', color: '#2563eb' }}>
                                Abrir módulo de orçamentos
                              </Button>
                            </Box>
                          </Collapse>
                        </Box>
                      );
                    })
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1, fontSize: '0.8rem' }}>Nenhum orçamento</Typography>
                  )}
                </Box>

                {/* Receituários */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MedicationIcon sx={{ color: '#7c3aed', fontSize: 18 }} />
                    Receituários Recentes
                  </Typography>
                  {summary?.recent_prescriptions && summary.recent_prescriptions.length > 0 ? (
                    summary.recent_prescriptions.map((p: any) => {
                      const rowKey = `rx:${p.id}`;
                      const isOpen = expandedHistoryRow === rowKey;
                      const meds = (p.items || []).slice().sort((a: any, x: any) => (a.sort_order ?? 0) - (x.sort_order ?? 0));
                      return (
                        <Box key={p.id} sx={{ py: 0.8, px: 1.5, borderRadius: 1.5, bgcolor: 'white', border: '1px solid #f1f5f9', mb: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#7c3aed', minWidth: 80 }}>
                              {p.prescription_number}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', minWidth: 70 }}>{formatDate(p.issue_date ?? p.prescription_date)}</Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', flex: 1, color: '#475569' }}>
                              {meds.length || 0} medicamento(s)
                            </Typography>
                            <Tooltip title={isOpen ? 'Recolher' : 'Ver medicamentos'}>
                              <IconButton size="small" onClick={() => setExpandedHistoryRow(isOpen ? null : rowKey)} sx={{ color: '#64748b', p: 0.3 }}>
                                {isOpen ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Enviar">
                              <IconButton size="small" onClick={() => {
                                setSendDocData({ id: p.id, patientId: linkedPatient?.id, type: 'prescription', label: 'Receituário' });
                                setSendDialogOpen(true);
                              }} sx={{ color: '#3b82f6', p: 0.3 }}>
                                <ShareIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Collapse in={isOpen}>
                            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #f1f5f9', bgcolor: '#faf5ff', mx: -1.5, px: 1.5, pb: 1, borderRadius: '0 0 8px 8px' }}>
                              {(p.diagnosis || p.diagnosis_code) && (
                                <Typography variant="caption" sx={{ display: 'block', color: '#6d28d9', mb: 0.75, fontWeight: 600 }}>
                                  {p.diagnosis_code ? `${p.diagnosis_code} — ` : ''}{p.diagnosis || ''}
                                </Typography>
                              )}
                              {meds.length === 0 ? (
                                <Typography variant="caption" color="text.secondary">Nenhum medicamento neste resumo.</Typography>
                              ) : (
                                meds.map((m: any, idx: number) => (
                                  <Box key={m.id || idx} sx={{ py: 0.4, borderBottom: idx < meds.length - 1 ? '1px dashed #e9d5ff' : 'none' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#5b21b6', display: 'block' }}>{m.medication_name || 'Medicamento'}</Typography>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.65rem' }}>
                                      {[m.dosage, m.frequency, m.duration].filter(Boolean).join(' · ')}
                                      {m.notes ? ` · ${m.notes}` : ''}
                                    </Typography>
                                  </Box>
                                ))
                              )}
                              <Button size="small" endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />} onClick={() => window.open('/clinic/prescriptions', '_blank')}
                                sx={{ mt: 1, textTransform: 'none', fontSize: '0.7rem', color: '#7c3aed' }}>
                                Abrir módulo de receituários
                              </Button>
                            </Box>
                          </Collapse>
                        </Box>
                      );
                    })
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1, fontSize: '0.8rem' }}>Nenhum receituário</Typography>
                  )}
                </Box>

                {/* Atestados */}
                {summary?.recent_certificates && summary.recent_certificates.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AssignmentIcon sx={{ color: '#dc2626', fontSize: 18 }} />
                      Atestados Recentes
                    </Typography>
                    {summary.recent_certificates.map((cert: any) => (
                      <Box key={cert.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.8, px: 1.5, borderRadius: 1.5, bgcolor: 'white', border: '1px solid #f1f5f9', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#dc2626', minWidth: 70 }}>
                          {formatDate(cert.issue_date)}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', flex: 1, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {cert.title || cert.certificate_type || 'Atestado'}
                        </Typography>
                        {cert.days_off && <Chip label={`${cert.days_off}d`} size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 600 }} />}
                        <Tooltip title="Enviar">
                          <IconButton size="small" onClick={() => {
                            setSendDocData({ id: cert.id, patientId: linkedPatient?.id, type: 'certificate', label: 'Atestado' });
                            setSendDialogOpen(true);
                          }} sx={{ color: '#3b82f6', p: 0.3 }}>
                            <ShareIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Pedidos de Exames */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ScienceIcon sx={{ color: '#059669', fontSize: 18 }} />
                    Pedidos de Exames
                  </Typography>
                  {summary?.recent_exam_orders && summary.recent_exam_orders.length > 0 ? (
                    summary.recent_exam_orders.map((e: any) => {
                      const rowKey = `ex:${e.id}`;
                      const isOpen = expandedHistoryRow === rowKey;
                      const exItems = (e.items || []).slice().sort((a: any, x: any) => (a.sort_order ?? 0) - (x.sort_order ?? 0));
                      return (
                        <Box key={e.id} sx={{ py: 0.8, px: 1.5, borderRadius: 1.5, bgcolor: 'white', border: '1px solid #f1f5f9', mb: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#059669', minWidth: 80 }}>
                              {e.order_number}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', minWidth: 70 }}>{formatDate(e.issue_date ?? e.order_date)}</Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', flex: 1, color: '#475569' }}>
                              {exItems.length || 0} exame(s)
                            </Typography>
                            <Tooltip title={isOpen ? 'Recolher' : 'Ver exames'}>
                              <IconButton size="small" onClick={() => setExpandedHistoryRow(isOpen ? null : rowKey)} sx={{ color: '#64748b', p: 0.3 }}>
                                {isOpen ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Collapse in={isOpen}>
                            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #f1f5f9', bgcolor: '#ecfdf5', mx: -1.5, px: 1.5, pb: 1, borderRadius: '0 0 8px 8px' }}>
                              {e.clinical_indication && (
                                <Typography variant="caption" sx={{ display: 'block', color: '#065f46', mb: 0.75, whiteSpace: 'pre-wrap' }}><strong>Indicação:</strong> {e.clinical_indication}</Typography>
                              )}
                              {e.urgency && (
                                <Chip label={e.urgency} size="small" sx={{ mb: 0.75, fontSize: '0.6rem', height: 20, bgcolor: '#fef3c7', color: '#92400e' }} />
                              )}
                              {exItems.length === 0 ? (
                                <Typography variant="caption" color="text.secondary">Nenhum exame neste resumo.</Typography>
                              ) : (
                                exItems.map((it: any, idx: number) => (
                                  <Box key={it.id || idx} sx={{ py: 0.35, borderBottom: idx < exItems.length - 1 ? '1px dashed #a7f3d0' : 'none' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#047857' }}>{it.exam_name || 'Exame'}</Typography>
                                    {it.exam_code && (
                                      <Typography component="span" variant="caption" sx={{ ml: 0.5, color: '#64748b' }}>({it.exam_code})</Typography>
                                    )}
                                    {it.price != null && Number(it.price) > 0 && (
                                      <Typography variant="caption" sx={{ display: 'block', color: '#64748b' }}>{formatCurrency(Number(it.price))}</Typography>
                                    )}
                                    {it.notes && <Typography variant="caption" sx={{ display: 'block', color: '#64748b', fontSize: '0.65rem' }}>{it.notes}</Typography>}
                                  </Box>
                                ))
                              )}
                              <Button size="small" endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />} onClick={() => window.open('/clinic/exam-orders', '_blank')}
                                sx={{ mt: 1, textTransform: 'none', fontSize: '0.7rem', color: '#059669' }}>
                                Abrir pedidos de exame
                              </Button>
                            </Box>
                          </Collapse>
                        </Box>
                      );
                    })
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1, fontSize: '0.8rem' }}>Nenhum pedido de exame</Typography>
                  )}
                </Box>

                {/* Planos de Tratamento */}
                {summary?.active_treatment_plans && summary.active_treatment_plans.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrendingIcon sx={{ color: '#14B8A6', fontSize: 18 }} />
                      Planos de Tratamento Ativos
                    </Typography>
                    {summary.active_treatment_plans.map((tp: any) => (
                      <Box key={tp.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.8, px: 1.5, borderRadius: 1.5, bgcolor: 'white', border: '1px solid rgba(20,184,166,0.15)', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#14B8A6' }}>
                          {tp.plan_number || tp.title}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', flex: 1 }}>{formatCurrency(tp.total_value)}</Typography>
                        <Chip label={getStatusConfig(tp.status).label} size="small"
                          sx={{ bgcolor: getStatusConfig(tp.status).bg, color: getStatusConfig(tp.status).color, fontSize: '0.6rem', height: 18, fontWeight: 600 }} />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* TAB 3 - PROCEDIMENTOS (planos / estoque) */}
            {activeTab === 3 && (
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MedicalIcon sx={{ color: '#0d9488', fontSize: 18 }} />
                    Procedimentos do plano
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => linkedPatient?.id && window.open(`/clinic/patients?id=${linkedPatient.id}`, '_blank')}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', borderColor: '#0d9488', color: '#0d9488' }}
                  >
                    Abrir no Prontuário
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Pendentes aguardam liberação; liberados podem ser executados no Prontuário (consome estoque); executados mostram materiais.
                </Typography>

                {(() => {
                  const pp = summary?.patient_procedures;
                  const approved = pp?.approved || [];
                  const pending = pp?.pending || [];
                  const completed = pp?.completed || [];
                  const renderMat = (item: any) => {
                    const mats = item.required_materials || [];
                    if (!mats.length) return null;
                    return (
                      <Box sx={{ mt: 0.75 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <InventoryIcon sx={{ fontSize: 12 }} /> Materiais
                        </Typography>
                        {mats.map((m: any) => {
                          const inv = m.clinic_inventory_items;
                          const needed = (m.quantity_per_execution || 1) * (item.quantity || 1);
                          const low = inv && Number(inv.current_stock) < needed;
                          return (
                            <Typography key={m.id} variant="caption" display="block" sx={{ ml: 1.5, color: low ? '#dc2626' : '#475569', fontSize: '0.65rem' }}>
                              • {inv?.name || '?'}: {needed} {inv?.unit || ''}
                              {low ? ` (est.: ${inv?.current_stock})` : ''}
                            </Typography>
                          );
                        })}
                      </Box>
                    );
                  };
                  return (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af', display: 'block', mb: 1 }}>
                          Liberados — aprovados para executar ({approved.length})
                        </Typography>
                        {approved.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Nenhum</Typography>
                        ) : (
                          approved.map((item: any) => (
                            <Box key={item.id} sx={{ py: 1, px: 1.5, mb: 0.5, borderRadius: 1.5, borderLeft: '3px solid #3b82f6', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.78rem' }}>{item.procedure_name}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                {item.plan_title || item.plan_number}
                                {item.budget_number && <> · Orç: <strong>{item.budget_number}</strong></>}
                                {' '}· Prof: {item.professional_name || '-'}
                              </Typography>
                              {renderMat(item)}
                            </Box>
                          ))
                        )}
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#92400e', display: 'block', mb: 1 }}>
                          Pendentes — aguardando liberação ({pending.length})
                        </Typography>
                        {pending.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Nenhum</Typography>
                        ) : (
                          pending.map((item: any) => (
                            <Box key={item.id} sx={{ py: 0.8, px: 1.5, mb: 0.5, borderRadius: 1.5, borderLeft: '3px solid #f59e0b', bgcolor: '#fffbeb', border: '1px solid #fef3c7' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.78rem' }}>{item.procedure_name}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                {item.plan_title || item.plan_number}
                                {item.budget_number && <> · Orç: <strong>{item.budget_number}</strong></>}
                                {' '}· Plano: {getStatusConfig(item.plan_status).label}
                              </Typography>
                            </Box>
                          ))
                        )}
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#065f46', display: 'block', mb: 1 }}>
                          Executados ({completed.length})
                        </Typography>
                        {completed.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Nenhum</Typography>
                        ) : (
                          completed.map((item: any) => {
                            const exec = item.execution;
                            const consumed: any[] = exec?.consumed_materials || [];
                            return (
                              <Box key={item.id} sx={{ py: 1, px: 1.5, mb: 0.5, borderRadius: 1.5, borderLeft: '3px solid #10b981', bgcolor: '#f0fdf4', border: '1px solid #d1fae5' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <CheckCircleIcon sx={{ fontSize: 14, color: '#059669' }} />
                                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.78rem' }}>{item.procedure_name}</Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                  {item.plan_title || item.plan_number}
                                  {item.budget_number && <> · Orç: <strong>{item.budget_number}</strong></>}
                                  {exec?.executed_at ? ` · ${new Date(exec.executed_at).toLocaleString('pt-BR')}` : ''}
                                </Typography>
                                {exec?.clinic_professionals?.full_name && (
                                  <Typography variant="caption" display="block" sx={{ fontSize: '0.65rem' }}>
                                    Prof: {exec.clinic_professionals.full_name}
                                  </Typography>
                                )}
                                {consumed.length > 0 && (
                                  <Box sx={{ mt: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 600 }}>Consumido:</Typography>
                                    {consumed.map((c: any, idx: number) => (
                                      <Typography key={idx} variant="caption" display="block" sx={{ ml: 1, fontSize: '0.65rem' }}>
                                        • {c.item_name}: {c.quantity} {c.unit}
                                      </Typography>
                                    ))}
                                  </Box>
                                )}
                              </Box>
                            );
                          })
                        )}
                      </Box>
                    </>
                  );
                })()}
              </Box>
            )}

            {/* TAB 4 - FINANCEIRO */}
            {activeTab === 4 && (
              <Box sx={{ p: 2 }}>
                <Grid container spacing={1.5} sx={{ mb: 3 }}>
                  <Grid item xs={4}>
                    <Card sx={{ borderRadius: 2, border: '1px solid #d1fae5', textAlign: 'center', bgcolor: '#f0fdf4' }}>
                      <CardContent sx={{ py: 1.5, px: 1, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="caption" sx={{ color: '#059669', fontWeight: 600, fontSize: '0.65rem' }}>Total Pago</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#059669', fontSize: '1rem' }}>
                          {formatCurrency(summary?.financial?.total_paid || 0)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={4}>
                    <Card sx={{ borderRadius: 2, border: '1px solid #fef3c7', textAlign: 'center', bgcolor: '#fffbeb' }}>
                      <CardContent sx={{ py: 1.5, px: 1, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="caption" sx={{ color: '#d97706', fontWeight: 600, fontSize: '0.65rem' }}>Pendente</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#d97706', fontSize: '1rem' }}>
                          {formatCurrency(summary?.financial?.total_pending || 0)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={4}>
                    <Card sx={{ borderRadius: 2, border: '1px solid #fee2e2', textAlign: 'center', bgcolor: '#fef2f2' }}>
                      <CardContent sx={{ py: 1.5, px: 1, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="caption" sx={{ color: '#dc2626', fontWeight: 600, fontSize: '0.65rem' }}>Em Atraso</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#dc2626', fontSize: '1rem' }}>
                          {formatCurrency(summary?.financial?.total_overdue || 0)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Card sx={{ borderRadius: 2, border: '1px solid rgba(20,184,166,0.15)', mb: 3, bgcolor: 'rgba(20,184,166,0.03)' }}>
                  <CardContent sx={{ py: 2, px: 2, '&:last-child': { pb: 2 }, textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.65rem' }}>Valor Total Investido</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#14B8A6' }}>
                      {formatCurrency(linkedPatient.total_spent || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      em {linkedPatient.total_visits || 0} consulta(s)
                    </Typography>
                  </CardContent>
                </Card>

                {/* Orçamentos pendentes */}
                {summary?.recent_budgets && summary.recent_budgets.filter((b: any) => b.status === 'sent' || b.status === 'draft').length > 0 && (
                  <Box sx={{ mb: 2.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#d97706', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WarningIcon sx={{ fontSize: 18 }} />
                      Orçamentos Pendentes
                    </Typography>
                    {summary.recent_budgets.filter((b: any) => b.status === 'sent' || b.status === 'draft').map((b: any) => (
                      <Box key={b.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.8, px: 1.5, borderRadius: 1.5, bgcolor: '#fffbeb', border: '1px solid #fef3c7', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#d97706' }}>
                          {b.budget_number}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', flex: 1, fontWeight: 600 }}>{formatCurrency(b.total)}</Typography>
                        <Chip label={getStatusConfig(b.status).label} size="small"
                          sx={{ bgcolor: getStatusConfig(b.status).bg, color: getStatusConfig(b.status).color, fontSize: '0.6rem', height: 18, fontWeight: 600 }} />
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Transações recentes */}
                {summary?.financial?.recent_transactions && summary.financial.recent_transactions.length > 0 && (
                  <Box sx={{ mb: 2.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MoneyIcon sx={{ fontSize: 18, color: '#14B8A6' }} />
                      Transações Recentes
                    </Typography>
                    {summary.financial.recent_transactions.map((t: any) => {
                      const isPaid = t.status === 'paid';
                      const isOverdue = t.status === 'overdue';
                      const statusColors: Record<string, { bg: string; color: string; label: string }> = {
                        paid: { bg: '#d1fae5', color: '#059669', label: 'Pago' },
                        pending: { bg: '#fef3c7', color: '#d97706', label: 'Pendente' },
                        overdue: { bg: '#fee2e2', color: '#dc2626', label: 'Atrasado' },
                        cancelled: { bg: '#f1f5f9', color: '#64748b', label: 'Cancelado' },
                        refunded: { bg: '#ede9fe', color: '#7c3aed', label: 'Estornado' },
                      };
                      const sc = statusColors[t.status] || statusColors.pending;
                      return (
                        <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.7, px: 1.5, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', mb: 0.5 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {t.description}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem' }}>
                              {t.category || t.transaction_type} {t.due_date ? `• Venc: ${new Date(t.due_date).toLocaleDateString('pt-BR')}` : ''}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: isPaid ? '#059669' : isOverdue ? '#dc2626' : '#0f172a', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                            {formatCurrency(isPaid ? (t.paid_amount || t.amount) : t.amount)}
                          </Typography>
                          <Chip label={sc.label} size="small" sx={{ bgcolor: sc.bg, color: sc.color, fontSize: '0.55rem', height: 18, fontWeight: 600 }} />
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* Descontos Pendentes */}
                {summary?.financial?.pending_discounts && summary.financial.pending_discounts.length > 0 && (
                  <Box sx={{ mb: 2.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#7c3aed', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ReceiptIcon sx={{ fontSize: 18, color: '#7c3aed' }} />
                      Descontos
                    </Typography>
                    {summary.financial.pending_discounts.map((d: any) => (
                      <Box key={d.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.7, px: 1.5, borderRadius: 1.5, bgcolor: '#faf5ff', border: '1px solid #e9d5ff', mb: 0.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                            {d.reason || 'Desconto'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem' }}>
                            {d.discount_type === 'percentage' ? `${d.discount_percentage}%` : 'Fixo'} sobre {formatCurrency(d.original_amount)}
                            {d.valid_until ? ` • Validade: ${new Date(d.valid_until).toLocaleDateString('pt-BR')}` : ''}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#7c3aed', fontSize: '0.8rem' }}>
                          -{formatCurrency(d.discount_amount)}
                        </Typography>
                        <Chip label={d.status === 'pending' ? 'Pendente' : d.status === 'applied' ? 'Aplicado' : d.status}
                          size="small" sx={{ bgcolor: d.status === 'applied' ? '#d1fae5' : '#fef3c7', color: d.status === 'applied' ? '#059669' : '#d97706', fontSize: '0.55rem', height: 18, fontWeight: 600 }} />
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Honorários */}
                {summary?.financial?.professional_fees && summary.financial.professional_fees.length > 0 && (
                  <Box sx={{ mb: 2.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2563eb', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MedicalIcon sx={{ fontSize: 18, color: '#2563eb' }} />
                      Honorários
                    </Typography>
                    {summary.financial.professional_fees.map((f: any) => (
                      <Box key={f.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.7, px: 1.5, borderRadius: 1.5, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', mb: 0.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                            {f.procedure_name || 'Procedimento'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem' }}>
                            {f.professional_name || ''} • {f.reference_month}/{f.reference_year}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#2563eb', fontSize: '0.8rem' }}>
                          {formatCurrency(f.gross_amount)}
                        </Typography>
                        <Chip label={f.payment_status === 'paid' ? 'Pago' : f.payment_status === 'pending' ? 'Pendente' : f.payment_status}
                          size="small" sx={{ bgcolor: f.payment_status === 'paid' ? '#d1fae5' : '#fef3c7', color: f.payment_status === 'paid' ? '#059669' : '#d97706', fontSize: '0.55rem', height: 18, fontWeight: 600 }} />
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Cheques Recebidos */}
                {summary?.financial?.checks_received && summary.financial.checks_received.length > 0 && (
                  <Box sx={{ mb: 2.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0891b2', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ReceiptIcon sx={{ fontSize: 18, color: '#0891b2' }} />
                      Cheques Recebidos
                    </Typography>
                    {summary.financial.checks_received.map((c: any) => (
                      <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.7, px: 1.5, borderRadius: 1.5, bgcolor: '#ecfeff', border: '1px solid #a5f3fc', mb: 0.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                            Cheque #{c.check_number}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem' }}>
                            {c.payer_name} {c.due_date ? `• Venc: ${new Date(c.due_date).toLocaleDateString('pt-BR')}` : ''}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0891b2', fontSize: '0.8rem' }}>
                          {formatCurrency(c.amount)}
                        </Typography>
                        <Chip label={c.status === 'compensated' ? 'Compensado' : c.status === 'pending' ? 'Pendente' : c.status === 'deposited' ? 'Depositado' : c.status}
                          size="small" sx={{ bgcolor: c.status === 'compensated' ? '#d1fae5' : '#fef3c7', color: c.status === 'compensated' ? '#059669' : '#d97706', fontSize: '0.55rem', height: 18, fontWeight: 600 }} />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}
            {/* TAB 5 - DOCUMENTOS */}
            {activeTab === 5 && (
              <Box sx={{ p: 2 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FolderIcon sx={{ color: '#2563eb', fontSize: 18 }} />
                    Documentos do Paciente
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<UploadFileIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setShowDocUpload(true)}
                    sx={{ bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' }, textTransform: 'none', fontSize: '0.75rem', borderRadius: 2, py: 0.5 }}
                  >
                    Anexar Documento
                  </Button>
                </Box>

                {/* Upload form */}
                {showDocUpload && (
                  <Card sx={{ mb: 2, borderRadius: 2, border: '1px solid rgba(37,99,235,0.2)', bgcolor: 'rgba(37,99,235,0.03)' }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#2563eb', mb: 1.5, display: 'block' }}>
                        Novo Documento
                      </Typography>
                      <TextField
                        fullWidth size="small" label="Título" placeholder="Ex: Contrato de tratamento 2025"
                        value={docUploadForm.title}
                        onChange={e => setDocUploadForm(p => ({ ...p, title: e.target.value }))}
                        sx={{ mb: 1.5 }}
                      />
                      <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                        <InputLabel>Tipo de Documento</InputLabel>
                        <Select
                          value={docUploadForm.document_type}
                          label="Tipo de Documento"
                          onChange={e => setDocUploadForm(p => ({ ...p, document_type: e.target.value }))}
                        >
                          <MenuItem value="contract">Contrato</MenuItem>
                          <MenuItem value="consent_form">Termo de Consentimento</MenuItem>
                          <MenuItem value="id_document">Documento de Identidade</MenuItem>
                          <MenuItem value="insurance_card">Carteirinha de Plano</MenuItem>
                          <MenuItem value="exam_result">Resultado de Exame</MenuItem>
                          <MenuItem value="xray">Raio-X / Imagem Clínica</MenuItem>
                          <MenuItem value="other">Outro</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth size="small" label="Descrição (opcional)"
                        value={docUploadForm.description}
                        onChange={e => setDocUploadForm(p => ({ ...p, description: e.target.value }))}
                        sx={{ mb: 1.5 }}
                      />
                      {/* File picker */}
                      <input
                        type="file"
                        ref={docFileInputRef}
                        style={{ display: 'none' }}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.txt,.xls,.xlsx"
                        onChange={e => {
                          const f = e.target.files?.[0];
                          if (f) {
                            setDocFile(f);
                            if (!docUploadForm.title) setDocUploadForm(p => ({ ...p, title: f.name }));
                          }
                        }}
                      />
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Button
                          size="small" variant="outlined"
                          startIcon={<AttachFileIcon sx={{ fontSize: 16 }} />}
                          onClick={() => docFileInputRef.current?.click()}
                          sx={{ borderColor: '#2563eb', color: '#2563eb', textTransform: 'none', fontSize: '0.75rem', borderRadius: 2 }}
                        >
                          {docFile ? docFile.name : 'Selecionar arquivo'}
                        </Button>
                        {docFile && (
                          <Typography variant="caption" color="text.secondary">
                            {(docFile.size / 1024).toFixed(0)} KB
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: 'flex-end' }}>
                        <Button size="small" onClick={() => { setShowDocUpload(false); setDocFile(null); }}
                          sx={{ textTransform: 'none', color: '#64748b' }}>Cancelar</Button>
                        <Button
                          size="small" variant="contained"
                          disabled={!docFile || docUploading}
                          onClick={handleDocUpload}
                          sx={{ bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' }, textTransform: 'none', borderRadius: 2 }}
                        >
                          {docUploading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Salvar'}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Documents list */}
                {docsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} sx={{ color: '#14B8A6' }} />
                  </Box>
                ) : documents.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <FolderIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                      Nenhum documento anexado
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Clique em "Anexar Documento" para adicionar contratos e arquivos do paciente
                    </Typography>
                  </Box>
                ) : (
                  documents.map((doc: any) => (
                    <Box key={doc.id} sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 1.5,
                      borderRadius: 2, bgcolor: 'white', border: '1px solid #f1f5f9', mb: 0.5,
                      '&:hover': { borderColor: '#e2e8f0', bgcolor: '#fafafa' },
                    }}>
                      <Box sx={{
                        width: 36, height: 36, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: `${getDocTypeColor(doc.document_type)}15`,
                      }}>
                        <FileIcon sx={{ fontSize: 18, color: getDocTypeColor(doc.document_type) }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {doc.title}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={getDocTypeLabel(doc.document_type)}
                            size="small"
                            sx={{ bgcolor: `${getDocTypeColor(doc.document_type)}15`, color: getDocTypeColor(doc.document_type), fontSize: '0.6rem', height: 16, fontWeight: 600 }}
                          />
                          {doc.file_size && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                              {(doc.file_size / 1024).toFixed(0)} KB
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Tooltip title="Baixar / Visualizar">
                        <IconButton
                          size="small"
                          onClick={() => window.open(doc.file_url, '_blank')}
                          sx={{ color: '#2563eb', '&:hover': { bgcolor: 'rgba(37,99,235,0.08)' } }}
                        >
                          <DownloadIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))
                )}
              </Box>
            )}

            {/* TAB 6 - IMAGENS */}
            {activeTab === 6 && (
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ImageIcon sx={{ color: '#14B8A6', fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                      Banco de Imagens Clínicas
                    </Typography>
                  </Box>
                  <Chip label={`${patientImages.length} imagens`} size="small" sx={{ bgcolor: 'rgba(20,184,166,0.1)', color: '#14B8A6', fontWeight: 600, fontSize: '0.7rem' }} />
                </Box>

                {imagesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} sx={{ color: '#14B8A6' }} />
                  </Box>
                ) : patientImages.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <ImageIcon sx={{ fontSize: 44, color: '#e2e8f0', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                      Nenhuma imagem clínica cadastrada
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                      Envie imagens pelo chat e salve-as aqui
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
                    {patientImages.map((img: any) => (
                      <Box
                        key={img.id}
                        sx={{
                          position: 'relative',
                          borderRadius: 2,
                          overflow: 'hidden',
                          border: '1.5px solid #e2e8f0',
                          cursor: 'pointer',
                          '&:hover .img-overlay': { opacity: 1 },
                          aspectRatio: '1',
                          bgcolor: '#f8fafc',
                        }}
                        onClick={() => setExpandedImage(img.file_url)}
                      >
                        <img
                          src={img.file_url}
                          alt={img.title || 'Imagem clínica'}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          onError={(e) => { (e.target as HTMLImageElement).src = 'https://via.placeholder.com/160?text=Erro'; }}
                        />
                        <Box
                          className="img-overlay"
                          sx={{
                            position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.45)',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            opacity: 0, transition: 'opacity 0.2s',
                          }}
                        >
                          <ZoomInIcon sx={{ color: '#fff', fontSize: 26 }} />
                          {img.title && (
                            <Typography variant="caption" sx={{ color: '#fff', fontSize: '0.65rem', mt: 0.5, px: 0.5, textAlign: 'center', lineHeight: 1.2 }}>
                              {img.title}
                            </Typography>
                          )}
                          {onSendMediaUrl && (
                            <Tooltip title="Enviar no chat">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSendMediaUrl(img.file_url, 'image', img.title || `imagem_clinica_${img.id}.jpg`);
                                }}
                                sx={{
                                  mt: 0.5, bgcolor: 'rgba(255,255,255,0.2)', color: '#fff',
                                  '&:hover': { bgcolor: 'rgba(37,211,102,0.7)' },
                                }}
                              >
                                <SendIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        {img.source === 'whatsapp_chat' && (
                          <Box sx={{
                            position: 'absolute', top: 4, right: 4,
                            bgcolor: 'rgba(20,184,166,0.85)', borderRadius: '50%', width: 18, height: 18,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <StarIcon sx={{ fontSize: 11, color: '#fff' }} />
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        ) : (
          /* ============================================
             NENHUM PACIENTE VINCULADO
             ============================================ */
          <Box sx={{ p: 3 }}>
            {/* Auto-matches */}
            {autoMatches.length > 0 && !showNewPatientForm && (
              <Box sx={{ mb: 3 }}>
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                  Encontramos {autoMatches.length} paciente(s) que podem corresponder a este contato:
                </Alert>
                {autoMatches.map((patient: any) => (
                  <Card key={patient.id} sx={{
                    mb: 1, borderRadius: 2, border: '1px solid rgba(20,184,166,0.15)', cursor: 'pointer',
                    '&:hover': { boxShadow: '0 2px 8px rgba(20,184,166,0.15)', borderColor: '#14B8A6' },
                  }}
                    onClick={() => handleLinkPatient(patient.id)}
                  >
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 }, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: '#14B8A6', width: 40, height: 40, fontSize: '0.9rem' }}>
                        {patient.full_name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{patient.full_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {patient.phone || patient.whatsapp || ''} {patient.cpf ? `• CPF: ${patient.cpf}` : ''}
                        </Typography>
                      </Box>
                      <Button size="small" variant="contained"
                        sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontSize: '0.7rem', borderRadius: 2 }}
                      >
                        <LinkIcon sx={{ fontSize: 16, mr: 0.5 }} /> Vincular
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}

            {/* Buscar paciente */}
            {!showNewPatientForm && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#0f172a' }}>
                  Buscar paciente para vincular
                </Typography>
                <TextField
                  fullWidth size="small"
                  placeholder="Buscar por nome, CPF, telefone..."
                  value={searchQuery}
                  onChange={(e) => handleSearchPatients(e.target.value)}
                  InputProps={{
                    startAdornment: searching ? <CircularProgress size={18} sx={{ mr: 1 }} /> : <PersonIcon sx={{ mr: 1, color: '#94a3b8', fontSize: 20 }} />,
                  }}
                  sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                {searchResults.length > 0 && (
                  <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                    {searchResults.map((patient: any) => (
                      <Box key={patient.id} sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 1.5,
                        borderRadius: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'rgba(20,184,166,0.06)' },
                      }}
                        onClick={() => handleLinkPatient(patient.id)}
                      >
                        <Avatar sx={{ bgcolor: '#14B8A6', width: 32, height: 32, fontSize: '0.75rem' }}>
                          {patient.full_name.charAt(0)}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{patient.full_name}</Typography>
                          <Typography variant="caption" color="text.secondary">{patient.phone || patient.cpf || ''}</Typography>
                        </Box>
                        {patient.contact_id && (
                          <Chip label="Já vinculado" size="small" sx={{ fontSize: '0.6rem', height: 18 }} />
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* Criar novo paciente */}
            {!showNewPatientForm ? (
              <Box sx={{ textAlign: 'center' }}>
                <Divider sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">ou</Typography>
                </Divider>
                <Button
                  variant="outlined"
                  startIcon={<PersonAddIcon />}
                  onClick={() => {
                    setNewPatientForm({
                      full_name: contactName || '',
                      cpf: '', birth_date: '', gender: '',
                      email: contactEmail || '',
                    });
                    setShowNewPatientForm(true);
                  }}
                  sx={{
                    borderColor: '#14B8A6', color: '#14B8A6', textTransform: 'none',
                    borderRadius: 2, fontWeight: 600, '&:hover': { borderColor: '#0d9488', bgcolor: 'rgba(20,184,166,0.04)' },
                  }}
                >
                  Cadastrar Novo Paciente
                </Button>
              </Box>
            ) : (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonAddIcon sx={{ color: '#14B8A6', fontSize: 20 }} />
                  Cadastrar Novo Paciente
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Nome Completo" required size="small" fullWidth
                    value={newPatientForm.full_name}
                    onChange={(e) => setNewPatientForm(f => ({ ...f, full_name: e.target.value }))}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="CPF" size="small" fullWidth
                      value={newPatientForm.cpf}
                      onChange={(e) => setNewPatientForm(f => ({ ...f, cpf: e.target.value }))}
                      helperText="Opcional — pode informar depois"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <TextField
                      label="Data de Nascimento" type="date" size="small" fullWidth
                      value={newPatientForm.birth_date}
                      onChange={(e) => setNewPatientForm(f => ({ ...f, birth_date: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      helperText="Opcional — pode informar depois"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Sexo</InputLabel>
                      <Select
                        value={newPatientForm.gender}
                        label="Sexo"
                        onChange={(e) => setNewPatientForm(f => ({ ...f, gender: e.target.value }))}
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="">Não informado</MenuItem>
                        <MenuItem value="masculine">Masculino</MenuItem>
                        <MenuItem value="feminine">Feminino</MenuItem>
                        <MenuItem value="other">Outro</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label="Email" size="small" fullWidth
                      value={newPatientForm.email}
                      onChange={(e) => setNewPatientForm(f => ({ ...f, email: e.target.value }))}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button onClick={() => setShowNewPatientForm(false)} sx={{ textTransform: 'none', color: '#64748b' }}>
                      Cancelar
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleCreatePatientFromContact}
                      disabled={!newPatientForm.full_name.trim()}
                      sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
                    >
                      Cadastrar e Vincular
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {/* ============================================
          DIALOG: AGENDAMENTO RÁPIDO
          ============================================ */}
      <Dialog open={showScheduleDialog} onClose={() => setShowScheduleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <CalendarIcon sx={{ color: '#14B8A6' }} />
          Agendar Consulta Rápida
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" sx={{ bgcolor: '#f0fdf4', p: 1.5, borderRadius: 2, border: '1px solid #d1fae5' }}>
              Paciente: <strong>{linkedPatient?.full_name}</strong>
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Data" type="date" size="small" fullWidth required
                value={scheduleForm.appointment_date}
                onChange={(e) => {
                  setScheduleForm(f => ({ ...f, appointment_date: e.target.value }));
                  loadDaySchedule(e.target.value);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                label="Horário" type="time" size="small" fullWidth required
                value={scheduleForm.start_time}
                onChange={(e) => setScheduleForm(f => ({ ...f, start_time: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={scheduleForm.appointment_type}
                  label="Tipo"
                  onChange={(e) => setScheduleForm(f => ({ ...f, appointment_type: e.target.value }))}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="consultation">Consulta</MenuItem>
                  <MenuItem value="return">Retorno</MenuItem>
                  <MenuItem value="procedure">Procedimento</MenuItem>
                  <MenuItem value="exam">Exame</MenuItem>
                  <MenuItem value="emergency">Urgência</MenuItem>
                  <MenuItem value="surgery">Cirurgia</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Duração</InputLabel>
                <Select
                  value={scheduleForm.duration_minutes}
                  label="Duração"
                  onChange={(e) => setScheduleForm(f => ({ ...f, duration_minutes: e.target.value as number }))}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value={15}>15 min</MenuItem>
                  <MenuItem value={30}>30 min</MenuItem>
                  <MenuItem value={45}>45 min</MenuItem>
                  <MenuItem value={60}>1 hora</MenuItem>
                  <MenuItem value={90}>1h30</MenuItem>
                  <MenuItem value={120}>2 horas</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <FormControl fullWidth size="small">
              <InputLabel>Profissional (opcional)</InputLabel>
              <Select
                value={scheduleForm.professional_id}
                label="Profissional (opcional)"
                onChange={(e) => setScheduleForm(f => ({ ...f, professional_id: e.target.value }))}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">Sem profissional</MenuItem>
                {professionals.map((p: any) => (
                  <MenuItem key={p.id} value={p.id}>
                    Dr(a). {p.full_name} {p.specialty ? `- ${p.specialty}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Cobrança</InputLabel>
              <Select
                label="Cobrança"
                value={scheduleForm.billing_mode}
                onChange={(e) =>
                  setScheduleForm(f => ({ ...f, billing_mode: e.target.value as 'particular' | 'plano' }))}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="particular">Particular</MenuItem>
                <MenuItem value="plano">Plano</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Observações" size="small" fullWidth multiline rows={2}
              value={scheduleForm.notes}
              onChange={(e) => setScheduleForm(f => ({ ...f, notes: e.target.value }))}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            {/* Mini agenda do dia */}
            {daySchedule.length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', mb: 0.5, display: 'block' }}>
                  Agenda do dia ({daySchedule.length} consulta(s)):
                </Typography>
                <Box sx={{ maxHeight: 120, overflowY: 'auto', bgcolor: '#f8fafc', borderRadius: 2, p: 1 }}>
                  {daySchedule.map((apt: any) => (
                    <Box key={apt.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.3 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#14B8A6', minWidth: 40 }}>
                        {apt.start_time?.substring(0, 5)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#475569', flex: 1 }}>
                        {apt.billing_mode === 'plano' ? 'Plano' : 'Particular'} ·{' '}
                        {apt.clinic_patients?.full_name || 'Paciente'} - {apt.title || apt.appointment_type}
                      </Typography>
                      <Chip label={getStatusConfig(apt.status).label} size="small"
                        sx={{ bgcolor: getStatusConfig(apt.status).bg, color: getStatusConfig(apt.status).color, fontSize: '0.55rem', height: 16 }} />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowScheduleDialog(false)} sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleQuickSchedule}
            disabled={!scheduleForm.appointment_date || !scheduleForm.start_time}
            startIcon={<EventIcon />}
            sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
          >
            Confirmar Agendamento
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================
          DIALOG: TEMPLATES DE MENSAGEM
          ============================================ */}
      <Dialog open={showTemplatesDialog} onClose={() => setShowTemplatesDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <SendIcon sx={{ color: '#14B8A6' }} />
          Mensagens de Saúde
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selecione um modelo para inserir no campo de mensagem:
          </Typography>
          {['agendamento', 'acompanhamento', 'financeiro', 'documentos', 'relacionamento'].map(category => {
            const categoryTemplates = messageTemplates.filter(t => t.category === category);
            if (categoryTemplates.length === 0) return null;
            const categoryLabels: Record<string, string> = {
              agendamento: '📅 Agendamento',
              acompanhamento: '🩺 Acompanhamento',
              financeiro: '💰 Financeiro',
              documentos: '📄 Documentos',
              relacionamento: '💚 Relacionamento',
            };
            return (
              <Box key={category} sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {categoryLabels[category] || category}
                </Typography>
                {categoryTemplates.map(template => (
                  <Card key={template.id} sx={{
                    mb: 1, borderRadius: 2, border: '1px solid #f1f5f9', cursor: 'pointer',
                    '&:hover': { borderColor: '#14B8A6', boxShadow: '0 2px 8px rgba(20,184,166,0.1)' },
                    transition: 'all 0.2s',
                  }}
                    onClick={() => handleSendTemplate(template)}
                  >
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>{template.name}</Typography>
                      <Typography variant="caption" sx={{ color: '#64748b', lineHeight: 1.4 }}>
                        {template.template.substring(0, 100)}...
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTemplatesDialog(false)} sx={{ textTransform: 'none' }}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <HealthcareEvolutionDialog
        open={evolutionDialogOpen}
        onClose={() => setEvolutionDialogOpen(false)}
        patient={linkedPatient}
        onSaved={() => {
          if (linkedPatient?.id) loadHealthcareSummary(linkedPatient.id);
          setSnackSeverity('success');
          setSnackMessage('Evolução criada com sucesso!');
        }}
        onError={(msg) => {
          setSnackSeverity('error');
          setSnackMessage(msg);
        }}
      />

      {/* Send Document Dialog */}
      <SendDocumentDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        documentType={sendDocData?.type || ''}
        documentId={sendDocData?.id || ''}
        patientId={sendDocData?.patientId || linkedPatient?.id}
        patientEmail={linkedPatient?.email}
        patientPhone={linkedPatient?.phone}
        patientWhatsapp={linkedPatient?.whatsapp}
        patientName={linkedPatient?.full_name}
        documentLabel={sendDocData?.label}
      />

      {/* Lightbox - Expanded image */}
      <Dialog
        open={!!expandedImage}
        onClose={() => setExpandedImage(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#000', borderRadius: 3 } }}
      >
        <DialogTitle sx={{ color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="subtitle2" sx={{ color: '#fff' }}>Imagem Clínica</Typography>
          <IconButton onClick={() => setExpandedImage(null)} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 1, textAlign: 'center' }}>
          {expandedImage && (
            <img
              src={expandedImage}
              alt="Imagem clínica expandida"
              style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', borderRadius: 8 }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 1.5, gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => expandedImage && window.open(expandedImage, '_blank')}
            sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', borderRadius: 2 }}
          >
            Abrir / Baixar
          </Button>
          {onSendMediaUrl && (
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={() => {
                if (expandedImage) {
                  onSendMediaUrl(expandedImage, 'image');
                  setExpandedImage(null);
                }
              }}
              sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' }, textTransform: 'none', borderRadius: 2 }}
            >
              Enviar no Chat
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default HealthcarePanel;
